import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { ManagementCompany } from "../Interfaces"
import useUser from "../hooks/useUser"
import { isConstructionWalk } from "../utils/publicSite"
import { canManageSageMasterData, isCompanyAdmin, SELECTED_COMPANY_STORAGE_KEY } from "../utils/companyAccess"
import { managementCompanyService } from "../services/managementCompanyService"

export interface SelectedCompanyContextValue {
  selectedCompany: ManagementCompany | null
  hasSelectedCompany: boolean
  isAdmin: boolean
  canManageSageMaster: boolean
  isReady: boolean
  selectCompany: (company: ManagementCompany) => void
  clearCompany: () => void
}

const SelectedCompanyContext = createContext<SelectedCompanyContextValue>({
  selectedCompany: null,
  hasSelectedCompany: false,
  isAdmin: false,
  canManageSageMaster: false,
  isReady: true,
  selectCompany: () => undefined,
  clearCompany: () => undefined,
})

function readStoredCompany(): ManagementCompany | null {
  if (typeof window === "undefined") return null
  try {
    const raw = sessionStorage.getItem(SELECTED_COMPANY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as ManagementCompany
    return parsed?.id ? parsed : null
  } catch {
    return null
  }
}

function writeStoredCompany(company: ManagementCompany | null) {
  if (typeof window === "undefined") return
  if (company) {
    sessionStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(company))
  } else {
    sessionStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY)
  }
}

export function clearStoredCompany() {
  writeStoredCompany(null)
}

export function SelectedCompanyProvider({ children }: { children: React.ReactNode }) {
  const user = useUser()
  const construction = isConstructionWalk()
  const isAdmin = construction && isCompanyAdmin(user.roleId)
  const canManageSageMaster = construction && canManageSageMasterData(user.roleId)
  const [selectedCompany, setSelectedCompany] = useState<ManagementCompany | null>(() => {
    return construction ? readStoredCompany() : null
  })
  const [isReady, setIsReady] = useState(!construction)

  const selectCompany = useCallback((company: ManagementCompany) => {
    setSelectedCompany(company)
    writeStoredCompany(company)
  }, [])

  const clearCompany = useCallback(() => {
    setSelectedCompany(null)
    writeStoredCompany(null)
  }, [])

  useEffect(() => {
    if (!construction || !user.id) {
      setIsReady(true)
      return
    }

    let cancelled = false

    const bind = async () => {
      if (isAdmin) {
        const stored = readStoredCompany()
        if (stored && !cancelled) setSelectedCompany(stored)
        if (!cancelled) setIsReady(true)
        return
      }

      const resolved = await managementCompanyService.resolveForUser(
        user.id,
        user.managementCompanyId || user.managementCompany?.id,
      )
      if (cancelled) return
      if (resolved) {
        setSelectedCompany(resolved)
        writeStoredCompany(resolved)
      } else if (user.managementCompany?.id) {
        setSelectedCompany(user.managementCompany)
        writeStoredCompany(user.managementCompany)
      }
      setIsReady(true)
    }

    bind()
    return () => {
      cancelled = true
    }
  }, [
    construction,
    isAdmin,
    user.id,
    user.managementCompanyId,
    user.managementCompany,
  ])

  const value = useMemo<SelectedCompanyContextValue>(() => ({
    selectedCompany,
    hasSelectedCompany: Boolean(selectedCompany?.id),
    isAdmin,
    canManageSageMaster,
    isReady,
    selectCompany,
    clearCompany,
  }), [selectedCompany, isAdmin, canManageSageMaster, isReady, selectCompany, clearCompany])

  return (
    <SelectedCompanyContext.Provider value={value}>
      {children}
    </SelectedCompanyContext.Provider>
  )
}

export function useSelectedCompany() {
  return useContext(SelectedCompanyContext)
}

export default SelectedCompanyContext
